/**
 * Account System (V3)
 *
 * V3-only account system with multi-role support.
 * Legacy schema support removed (2026-02-18) - unsupported schemas reset to fresh V3 state.
 */

import { AppState } from '../types'
import { AccountsState } from '../types/accounts'
import {
  createInitialAccountsState,
  createInitialAccountsStateWithAll,
  validateAccountsState,
} from './accountHelpers'
import { debug, warn, error, safeJsonParse } from './logger'
import { safeStorageWrite } from './storageQuota'
export const CURRENT_SCHEMA_VERSION = 3
interface LegacyPlacementResult {
  moduleId: string
  group: number
  startingLevelId?: number
  startingTier?: number
  score: number
  totalQuestions: number
  completedAt: number
}
function normalizePlacementResults(appState: AppState): AppState {
  if (!appState.placementResults) {
    return appState
  }
  const placementResults = Object.fromEntries(
    Object.entries(appState.placementResults).map(([key, result]) => {
      const typedResult = result as LegacyPlacementResult
      const startingTier =
        typedResult.startingTier ?? typedResult.startingLevelId
      if (startingTier === undefined) {
        return [key, result]
      }
      return [
        key,
        {
          ...result,
          startingTier,
        },
      ]
    }),
  ) as AppState['placementResults']
  return {
    ...appState,
    placementResults,
  }
}
function normalizeV3State(state: MultiAccountStateV3): MultiAccountStateV3 {
  const learnerData = Object.fromEntries(
    Object.entries(state.learnerData).map(([learnerId, appState]) => [
      learnerId,
      normalizePlacementResults(appState),
    ]),
  ) as MultiAccountStateV3['learnerData']
  return {
    ...state,
    learnerData,
  }
}

/**
 * Multi-Account State V3 (with accounts)
 */
export interface MultiAccountStateV3 {
  schemaVersion: 3
  accounts: AccountsState
  learnerData: Record<string, AppState>
}

/**
 * Load state from localStorage (V3 only).
 *
 * Unsupported schemas are discarded and reset to fresh V3 state.
 */
export function loadStateV3(storageKey: string): MultiAccountStateV3 {
  try {
    const stored = localStorage.getItem(storageKey)
    if (!stored) {
      debug('No stored state found - creating fresh V3 state')
      return createFreshV3State(true)
    }
    const parsed = safeJsonParse(stored, null, 'loadStateV3')
    if (!parsed) {
      warn('Failed to parse stored state - creating fresh V3 state')
      return createFreshV3State(true)
    }

    // Only support V3 format
    if (parsed.schemaVersion === 3) {
      if (validateAccountsState(parsed.accounts)) {
        debug('Loaded existing V3 (accounts) state')
        return normalizeV3State(parsed as MultiAccountStateV3)
      } else {
        warn('Invalid V3 accounts state structure - unsupported schema reset')
        return createFreshV3State(true)
      }
    }
    warn(
      `Unsupported schema reset (schema: ${parsed.schemaVersion || 'unknown'}) - creating fresh V3 state.`,
    )
    return createFreshV3State(true)
  } catch (err) {
    error('Failed to load state:', err)
    return createFreshV3State(true)
  }
}

/**
 * Create fresh V3 state
 */
export function createFreshV3State(
  includeParentAndTeacher: boolean = true,
): MultiAccountStateV3 {
  const accountsState = includeParentAndTeacher
    ? createInitialAccountsStateWithAll()
    : createInitialAccountsState()
  const learnerId = accountsState.activeAccountId
  const initialLearnerData: AppState = {
    stats: {
      name: 'Leerling',
      xp: 0,
      level: 1,
    },
    progress: {
      Metriek: {
        cheatSheetViewed: false,
      },
      Getalbegrip: {
        cheatSheetViewed: false,
      },
      TijdGeld: {
        cheatSheetViewed: false,
      },
      SpellingWW: {
        cheatSheetViewed: false,
      },
      SpellingRegel: {
        cheatSheetViewed: false,
      },
      Taalbegrip: {
        cheatSheetViewed: false,
      },
      Studievaardig: {
        cheatSheetViewed: false,
      },
      Topo: {
        cheatSheetViewed: false,
      },
    },
    questionStates: {},
    pendingLevelUp: undefined,
    placementResults: {},
    familyMastery: {},
  }
  return {
    schemaVersion: 3,
    accounts: accountsState,
    learnerData: {
      [learnerId]: initialLearnerData,
    },
  }
}

/**
 * Save V3 state to localStorage
 */
export function saveV3State(
  storageKey: string,
  state: MultiAccountStateV3,
): void {
  const serialized = JSON.stringify(state)
  const success = safeStorageWrite(storageKey, serialized)
  if (!success) {
    error(
      'Failed to save V3 state: localStorage quota exceeded. Data may be lost.',
    )
  }
}
